import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Card, LoadingState, ErrorState, Pill, CopperButton, SlateButton, Modal } from "@/components/common/UI";
import AuditTrail from "@/components/common/AuditTrail";
import { useAuth, hasRole } from "@/lib/auth";
import { fmtINR, fmtDate, fmtPct, fmtRelative } from "@/lib/format";
import { ArrowLeft, Edit, Copy, Send, CheckCircle2, XCircle, AlertTriangle, ArrowUpRight, RotateCcw, Sparkles, FileSpreadsheet, FileText, Layers, Trophy, ThumbsDown, MinusCircle } from "lucide-react";
import { toast } from "sonner";

const STATUS_TONE = {
    DRAFT: { tone: "slate", label: "Draft" },
    PENDING_DH: { tone: "amber", label: "Pending DH" },
    PENDING_DIRECTOR: { tone: "copper", label: "Pending Director" },
    APPROVED: { tone: "emerald", label: "Approved" },
    DIRECTOR_APPROVED: { tone: "emerald", label: "Director Approved" },
    HELD: { tone: "red", label: "Held" },
};

export default function RFQDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [r, setR] = useState(null);
    const [error, setError] = useState("");
    const [commentModal, setCommentModal] = useState(null);
    const [comment, setComment] = useState("");
    const [whatIfOpen, setWhatIfOpen] = useState(false);
    const [volumeTiersOpen, setVolumeTiersOpen] = useState(false);
    const [settingOutcome, setSettingOutcome] = useState(false);
    const [priceImpact, setPriceImpact] = useState(null);
    const [impactModalOpen, setImpactModalOpen] = useState(false);
    const [repricing, setRepricing] = useState(false);

    const load = () => api.get(`/rfqs/${id}`).then((x) => setR(x.data)).catch((e) => setError(formatApiError(e)));
    useEffect(() => { load(); }, [id]);

    // Requirement #6: detect whether a steel-rate change (or any other ZBC
    // recompute) has moved a part's should-cost since this RFQ was priced.
    // Only worth checking while the RFQ is still being prepared; a finally
    // decided RFQ's price is a historical record, not something to flag.
    useEffect(() => {
        if (!r || !["DRAFT", "HELD", "PENDING_DH", "PENDING_DIRECTOR"].includes(r.status)) { setPriceImpact(null); return; }
        api.get(`/rfqs/${id}/price-impact`).then((x) => setPriceImpact(x.data)).catch(() => {});
    }, [r, id]);

    const reprice = async () => {
        setRepricing(true);
        try {
            await api.post(`/rfqs/${id}/reprice`);
            toast.success("RFQ re-priced with current rates");
            setImpactModalOpen(false);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setRepricing(false); }
    };

    const canEdit = hasRole(user, "CORPORATE_ADMIN", "FINANCE_PRICING");
    const canDhAct = hasRole(user, "DEPT_HEAD", "CORPORATE_ADMIN") && r?.status === "PENDING_DH";
    const canDirAct = hasRole(user, "DIRECTOR", "CORPORATE_ADMIN") && r?.status === "PENDING_DIRECTOR";

    const submit = async () => {
        try { const { data } = await api.post(`/rfqs/${id}/submit`); toast.success(`Submitted → ${data.new_status} (margin ${data.margin_pct}%)`); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };
    const withdraw = async () => {
        try { await api.post(`/rfqs/${id}/withdraw`); toast.success("Withdrawn"); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };
    const dhApprove = async () => {
        try { await api.post(`/rfqs/${id}/dh-approve`); toast.success("Approved"); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };
    const doComment = async () => {
        const m = commentModal;
        if (!m || !comment.trim()) { toast.error("Comment required"); return; }
        try {
            await api.post(`/rfqs/${id}/${m.endpoint}`, { comment });
            toast.success(m.success);
            setCommentModal(null); setComment(""); load();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const setOutcome = async (outcome) => {
        setSettingOutcome(true);
        try {
            await api.post(`/rfqs/${id}/outcome`, { outcome });
            toast.success(`Marked ${outcome}`);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSettingOutcome(false); }
    };
    const duplicate = async () => {
        try { const { data } = await api.post(`/rfqs/${id}/duplicate`); toast.success(`Duplicated as ${data.reference}`); navigate(`/rfq/${data.id}/edit`); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    // P0.4 · Download the child-parts Excel breakdown
    const downloadChildPartsExcel = async () => {
        try {
            const res = await api.get(`/rfqs/${id}/child-parts-export`, { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `RFQ_${r?.reference || id}_ChildParts.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success("Child-parts Excel downloaded");
        } catch (e) { toast.error(formatApiError(e)); }
    };

    // Customer-facing commercial quotation letter (PDF)
    const downloadQuotationPdf = async () => {
        try {
            const res = await api.get(`/rfqs/${id}/quotation.pdf`, { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `${r?.reference || id}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success("Quotation PDF downloaded");
        } catch (e) { toast.error(formatApiError(e)); }
    };

    if (error) return <ErrorState message={error} />;
    if (!r) return <LoadingState />;
    const st = STATUS_TONE[r.status] || { tone: "slate", label: r.status };
    const p = r.pricing || {};
    const isFinal = ["APPROVED", "DIRECTOR_APPROVED"].includes(r.status);

    return (
        <div data-testid="rfq-detail-page">
            <div className="mb-4"><Link to="/rfq" className="text-[12px] text-slate-500 hover:text-[hsl(var(--copper))] inline-flex items-center gap-1"><ArrowLeft className="h-3 w-3" /> Back to RFQs</Link></div>

            <Card className="p-5 mb-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2">
                            <span className="font-mono text-sm font-bold">{r.reference}</span>
                            <Pill tone={st.tone}>{st.label}</Pill>
                            <Pill tone="slate">{r.formula_variant}</Pill>
                            {p.below_dh_threshold && <Pill tone="red"><AlertTriangle className="h-2.5 w-2.5" /> Below DH threshold</Pill>}
                        </div>
                        <h1 className="font-display text-2xl font-semibold mb-2">{r.customer_name}</h1>
                        <div className="text-[12px] text-slate-600">
                            {(r.parts || []).length} parts · Created {fmtDate(r.created_at)} by {r.created_by_name} · Due {fmtDate(r.response_due_date)}
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {canEdit && ["DRAFT", "HELD"].includes(r.status) && (
                            <>
                                <SlateButton onClick={() => navigate(`/rfq/${id}/edit`)} variant="outline"><Edit className="h-4 w-4" /> Edit</SlateButton>
                                <CopperButton onClick={submit}><Send className="h-4 w-4" /> Submit</CopperButton>
                            </>
                        )}
                        {canEdit && ["PENDING_DH", "PENDING_DIRECTOR"].includes(r.status) && (
                            <SlateButton onClick={withdraw} variant="outline"><RotateCcw className="h-4 w-4" /> Withdraw</SlateButton>
                        )}
                        <SlateButton onClick={duplicate} variant="outline"><Copy className="h-4 w-4" /> Duplicate</SlateButton>
                        <SlateButton onClick={downloadQuotationPdf} variant="outline" data-testid="rfq-quotation-pdf">
                            <FileText className="h-4 w-4" /> Quotation (.pdf)
                        </SlateButton>
                        <SlateButton onClick={downloadChildPartsExcel} variant="outline" data-testid="rfq-child-parts-excel">
                            <FileSpreadsheet className="h-4 w-4" /> Child Parts (.xlsx)
                        </SlateButton>
                        <SlateButton onClick={() => setWhatIfOpen(true)} variant="outline" data-testid="rfq-whatif-btn">
                            <Sparkles className="h-4 w-4" /> What-If
                        </SlateButton>
                        <SlateButton onClick={() => setVolumeTiersOpen(true)} variant="outline" data-testid="rfq-volume-tiers-btn">
                            <Layers className="h-4 w-4" /> Volume Tiers
                        </SlateButton>
                    </div>
                </div>
            </Card>

            {/* Stale-price warning: requirement #6, a steel-rate change on one of
                this RFQ's ZBCs since it was priced. */}
            {priceImpact?.stale && (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-4 mb-4 flex items-start gap-3" data-testid="rfq-stale-price-banner">
                    <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                    <div className="flex-1">
                        <div className="text-sm font-medium text-amber-900">
                            Price is out of date: {priceImpact.parts_changed.length} part{priceImpact.parts_changed.length > 1 ? "s have" : " has"} a should-cost change since this RFQ was priced.
                        </div>
                        <div className="text-xs text-amber-800 mt-1">
                            Current price would be {fmtINR(priceImpact.new_final_price)} ({priceImpact.price_delta >= 0 ? "+" : ""}{fmtINR(priceImpact.price_delta)} vs. the {fmtINR(priceImpact.old_final_price)} shown below).
                        </div>
                    </div>
                    <SlateButton onClick={() => setImpactModalOpen(true)} variant="outline" className="h-8 text-[12px] shrink-0" data-testid="rfq-view-impact-btn">
                        View Impact
                    </SlateButton>
                </div>
            )}

            {/* Summary cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
                <Card className="p-4 bg-[hsl(var(--copper-soft))] border-[hsl(var(--copper))]/30"><div className="eyebrow text-slate-600">FINAL PRICE</div><div className="mt-1 font-mono tabular-nums text-xl font-semibold text-[hsl(var(--copper))]">{fmtINR(p.final_price || 0)}</div></Card>
                <Card className="p-4"><div className="eyebrow text-slate-500">PARTS</div><div className="mt-1 font-mono tabular-nums text-xl font-semibold">{(r.parts || []).length}</div></Card>
                <Card className="p-4"><div className="eyebrow text-slate-500">MARGIN</div><div className={`mt-1 font-mono tabular-nums text-xl font-semibold ${p.margin_pct >= 15 ? "text-emerald-600" : p.margin_pct >= 8 ? "text-amber-600" : "text-red-600"}`}>{fmtPct(p.margin_pct || 0)}</div></Card>
                <Card className="p-4"><div className="eyebrow text-slate-500">FORMULA USED</div><div className="mt-1 font-mono text-sm font-semibold">{r.formula_variant}</div></Card>
            </div>

            {/* DH / Director approval panel */}
            {(canDhAct || canDirAct) && (
                <Card className="p-5 mb-4 border-[hsl(var(--copper))]/30 bg-[hsl(var(--copper-soft))]">
                    <div className="eyebrow text-slate-600 mb-2">REQUIRES YOUR DECISION</div>
                    <p className="text-sm text-slate-700 mb-3">
                        Margin: <span className="font-semibold">{fmtPct(p.margin_pct)}</span>.
                        {p.below_dh_threshold && canDhAct && " Below 8% threshold: Director can approve below threshold."}
                        {canDirAct && " Escalated for below-threshold approval."}
                    </p>
                    <div className="flex flex-wrap gap-2">
                        {canDhAct && <CopperButton onClick={dhApprove}><CheckCircle2 className="h-4 w-4" /> Approve</CopperButton>}
                        {canDhAct && <SlateButton variant="outline" onClick={() => setCommentModal({ endpoint: "dh-hold", title: "Hold RFQ", success: "Held, Finance notified" })}><XCircle className="h-4 w-4" /> Hold with Comment</SlateButton>}
                        {canDhAct && <SlateButton variant="outline" onClick={() => setCommentModal({ endpoint: "escalate", title: "Escalate to Director", success: "Escalated" })}><ArrowUpRight className="h-4 w-4" /> Escalate to Director</SlateButton>}
                        {canDirAct && <CopperButton onClick={() => setCommentModal({ endpoint: "director-approve", title: "Director Approval Below Threshold", success: "Director approved" })}><CheckCircle2 className="h-4 w-4" /> Approve Anyway</CopperButton>}
                        {canDirAct && <SlateButton variant="outline" onClick={() => setCommentModal({ endpoint: "director-reject", title: "Reject · Send Back", success: "Sent back" })}><XCircle className="h-4 w-4" /> Reject · Send Back</SlateButton>}
                    </div>
                </Card>
            )}

            {/* Customer outcome: recordable once internal approval is done */}
            {["DIRECTOR_APPROVED", "APPROVED"].includes(r.status) && canEdit && (
                <Card className="p-5 mb-4">
                    <div className="eyebrow text-slate-500 mb-2">CUSTOMER OUTCOME</div>
                    {r.customer_outcome ? (
                        <div className="flex items-center gap-2 text-[13px]">
                            <Pill tone={r.customer_outcome === "WON" ? "emerald" : r.customer_outcome === "LOST" ? "red" : "amber"}>
                                {r.customer_outcome}
                            </Pill>
                            <span className="text-slate-500">recorded {fmtRelative(r.outcome_at)} by {r.outcome_by_name}</span>
                        </div>
                    ) : (
                        <>
                            <p className="text-[12px] text-slate-500 mb-3">
                                What happened with the customer after approval? Feeds win-rate analytics; not required, but nothing else in the system captures this.
                            </p>
                            <div className="flex flex-wrap gap-2">
                                <CopperButton onClick={() => setOutcome("WON")} disabled={settingOutcome} data-testid="rfq-outcome-won">
                                    <Trophy className="h-4 w-4" /> Won
                                </CopperButton>
                                <SlateButton variant="outline" onClick={() => setOutcome("LOST")} disabled={settingOutcome} data-testid="rfq-outcome-lost">
                                    <ThumbsDown className="h-4 w-4" /> Lost
                                </SlateButton>
                                <SlateButton variant="outline" onClick={() => setOutcome("PARTIAL")} disabled={settingOutcome} data-testid="rfq-outcome-partial">
                                    <MinusCircle className="h-4 w-4" /> Partial
                                </SlateButton>
                            </div>
                        </>
                    )}
                </Card>
            )}

            {/* Parts table */}
            <Card className="p-5 mb-4">
                <div className="eyebrow text-slate-500 mb-3">PARTS · ZBC ROLL-UP</div>
                <table className="w-full text-[12px]">
                    <thead><tr className="border-b border-slate-200 text-slate-500"><th className="text-left py-2">Part Number</th><th className="text-left py-2">Name</th><th className="text-right py-2">Should-Cost</th><th className="text-right py-2">Qty</th><th className="text-right py-2">Vol/yr</th></tr></thead>
                    <tbody>
                        {(r.parts || []).map((pp, i) => (
                            <tr key={i} className="border-b border-slate-100">
                                <td className="py-1.5 font-mono font-semibold">{pp.part_number}</td>
                                <td className="py-1.5">{pp.part_name}</td>
                                <td className="py-1.5 text-right font-mono tabular-nums">{fmtINR(pp.should_cost)}</td>
                                <td className="py-1.5 text-right font-mono tabular-nums">{pp.qty_per_assy}</td>
                                <td className="py-1.5 text-right font-mono tabular-nums">{pp.annual_volume?.toLocaleString("en-IN") || 0}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </Card>

            {/* Full price summary */}
            <Card className="p-5 mb-4">
                <div className="eyebrow text-slate-500 mb-3">PRICE BUILD-UP</div>
                <table className="w-full text-[13px]">
                    <tbody>
                        <tr><td className="py-1 text-slate-600">RM + BOP</td><td className="text-right font-mono tabular-nums">{fmtINR(p.rm_bop_total || 0)}</td></tr>
                        <tr><td className="py-1 text-slate-600">Process (Conv + Cut + Weld)</td><td className="text-right font-mono tabular-nums">{fmtINR(p.process_total || 0)}</td></tr>
                        <tr className="border-t border-slate-200"><td className="py-1.5 font-semibold">Cost Total</td><td className="text-right font-mono tabular-nums font-semibold">{fmtINR(p.cost_total || 0)}</td></tr>
                        <tr><td className="py-1 text-slate-500 text-[11px]">+ Overhead ({p.overhead_pct}%)</td><td className="text-right font-mono tabular-nums text-[11px]">{fmtINR(p.overhead_amount || 0)}</td></tr>
                        <tr><td className="py-1 text-slate-500 text-[11px]">+ ICC ({p.icc_pct}%)</td><td className="text-right font-mono tabular-nums text-[11px]">{fmtINR(p.icc_amount || 0)}</td></tr>
                        <tr><td className="py-1 text-slate-500 text-[11px]">+ Rejection</td><td className="text-right font-mono tabular-nums text-[11px]">{fmtINR(p.rejection_amount || 0)}</td></tr>
                        {p.tool_amount > 0 && <tr><td className="py-1 text-slate-500 text-[11px]">+ Tool Maint</td><td className="text-right font-mono tabular-nums text-[11px]">{fmtINR(p.tool_amount)}</td></tr>}
                        <tr><td className="py-1 text-slate-500 text-[11px]">+ Profit ({p.profit_label})</td><td className="text-right font-mono tabular-nums text-[11px]">{fmtINR(p.profit_amount || 0)}</td></tr>
                        <tr className="border-t border-slate-300"><td className="py-2 font-semibold">Grand Total</td><td className="text-right font-mono tabular-nums font-semibold">{fmtINR(p.grand_total || 0)}</td></tr>
                        {p.discount_amount > 0 && <tr><td className="py-1 text-slate-500">− Discount</td><td className="text-right font-mono tabular-nums text-slate-500">({fmtINR(p.discount_amount)})</td></tr>}
                        {p.volume_rebate > 0 && <tr><td className="py-1 text-slate-500">− Volume Rebate</td><td className="text-right font-mono tabular-nums text-slate-500">({fmtINR(p.volume_rebate)})</td></tr>}
                        <tr className="border-t-2 border-slate-900 bg-[hsl(var(--copper-soft))]"><td className="py-3 font-display text-base font-semibold">FINAL PRICE EX-WORKS</td><td className="text-right font-mono tabular-nums font-bold text-2xl text-[hsl(var(--copper))]">{fmtINR(p.final_price || 0)}</td></tr>
                    </tbody>
                </table>
            </Card>

            {/* Negotiation history: one frozen snapshot per submission round */}
            {(r.versions || []).length > 1 && (
                <Card className="p-5 mb-4">
                    <div className="eyebrow text-slate-500 mb-3">NEGOTIATION HISTORY · {r.versions.length} ROUNDS</div>
                    <table className="w-full text-[12px]">
                        <thead>
                            <tr className="border-b border-slate-200 text-slate-500">
                                <th className="text-left py-2">Round</th>
                                <th className="text-left py-2">Submitted</th>
                                <th className="text-right py-2">Final Price</th>
                                <th className="text-right py-2">Δ vs Prior</th>
                                <th className="text-right py-2">Margin</th>
                            </tr>
                        </thead>
                        <tbody>
                            {[...r.versions].reverse().map((v, i) => {
                                const idx = r.versions.length - i; // original 1-based index
                                const prior = idx > 1 ? r.versions[idx - 2] : null;
                                const delta = prior ? (v.pricing?.final_price || 0) - (prior.pricing?.final_price || 0) : null;
                                const isCurrent = idx === r.current_version;
                                return (
                                    <tr key={v.version} className={`border-b border-slate-100 ${isCurrent ? "bg-[hsl(var(--copper-soft))]/40" : ""}`}>
                                        <td className="py-1.5 font-mono font-semibold">v{v.version}{isCurrent && <span className="ml-1.5"><Pill tone="copper">current</Pill></span>}</td>
                                        <td className="py-1.5 text-slate-600">{fmtRelative(v.submitted_at)} · {v.submitted_by_name}</td>
                                        <td className="py-1.5 text-right font-mono tabular-nums">{fmtINR(v.pricing?.final_price || 0)}</td>
                                        <td className={`py-1.5 text-right font-mono tabular-nums ${delta > 0 ? "text-red-600" : delta < 0 ? "text-emerald-600" : "text-slate-400"}`}>
                                            {delta === null ? "—" : `${delta > 0 ? "+" : ""}${fmtINR(delta)}`}
                                        </td>
                                        <td className="py-1.5 text-right font-mono tabular-nums">{fmtPct(v.pricing?.margin_pct || 0)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </Card>
            )}

            {/* Approval timeline */}
            {(r.approval_history || []).length > 0 && (
                <Card className="p-5">
                    <div className="eyebrow text-slate-500 mb-3">APPROVAL TIMELINE</div>
                    <div className="space-y-2">
                        {r.approval_history.map((h, i) => (
                            <div key={i} className="flex items-start gap-2.5">
                                <div className={`h-6 w-6 rounded-full flex items-center justify-center text-[10px] font-mono shrink-0 ${h.action === "APPROVED" || h.action === "DIRECTOR_APPROVED" ? "bg-emerald-500 text-white" : h.action === "HELD" || h.action === "REJECTED" ? "bg-red-500 text-white" : h.action === "ESCALATED" ? "bg-amber-500 text-white" : "bg-slate-300 text-slate-700"}`}>{i+1}</div>
                                <div className="flex-1 text-[12px]">
                                    <div><span className="font-semibold">{h.action.replace("_", " ")}</span> by {h.by_name} ({h.by_role}) · <span className="text-slate-500">{fmtRelative(h.at)}</span></div>
                                    {h.comment && <div className="text-slate-600 italic mt-1">&ldquo;{h.comment}&rdquo;</div>}
                                </div>
                            </div>
                        ))}
                    </div>
                </Card>
            )}

            <Modal open={!!commentModal} onClose={() => { setCommentModal(null); setComment(""); }} title={commentModal?.title || ""}
                footer={<>
                    <SlateButton variant="outline" onClick={() => { setCommentModal(null); setComment(""); }}>Cancel</SlateButton>
                    <CopperButton onClick={doComment}>Submit</CopperButton>
                </>}>
                <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={4} className="w-full px-3 py-2 rounded-md border border-slate-200 text-sm" placeholder="Add comment for next role…" autoFocus />
            </Modal>

            <div className="mt-4">
                <AuditTrail entityType="RFQ" entityId={r.id} />
            </div>

            <WhatIfModal open={whatIfOpen} onClose={() => setWhatIfOpen(false)} rfq={r} />
            <VolumeTiersModal open={volumeTiersOpen} onClose={() => setVolumeTiersOpen(false)} rfq={r} />
            <PriceImpactModal
                open={impactModalOpen}
                onClose={() => setImpactModalOpen(false)}
                impact={priceImpact}
                onReprice={reprice}
                repricing={repricing}
            />
        </div>
    );
}

function PriceImpactModal({ open, onClose, impact, onReprice, repricing }) {
    if (!impact) return null;
    return (
        <Modal open={open} onClose={onClose} title="Price Impact · Steel Rate Change" size="lg">
            <div className="space-y-4">
                <p className="text-[12.5px] text-slate-600">
                    One or more of this RFQ&#39;s parts had their ZBC should-cost recomputed (usually a steel-rate
                    change) after this RFQ was priced. Nothing is changed automatically: review the impact below,
                    then choose to re-price or leave it as-is for now.
                </p>

                <div className="rounded-md border border-slate-200 overflow-hidden">
                    <div className="grid grid-cols-2 divide-x divide-slate-200">
                        <div className="px-4 py-3 bg-slate-50">
                            <div className="eyebrow text-slate-500">CURRENT PRICE (STORED)</div>
                            <div className="font-mono tabular-nums text-lg font-semibold">{fmtINR(impact.old_final_price)}</div>
                            <div className="text-[11px] text-slate-500 mt-0.5">Margin {fmtPct(impact.old_margin_pct || 0)}</div>
                        </div>
                        <div className="px-4 py-3 bg-[hsl(var(--copper-soft))]">
                            <div className="eyebrow text-slate-600">IF RE-PRICED TODAY</div>
                            <div className="font-mono tabular-nums text-lg font-semibold text-[hsl(var(--copper))]">{fmtINR(impact.new_final_price)}</div>
                            <div className="text-[11px] text-slate-600 mt-0.5">Margin {fmtPct(impact.new_margin_pct || 0)}</div>
                        </div>
                    </div>
                    <div className="px-4 py-3 border-t border-slate-200">
                        <div className="eyebrow text-slate-500">PRICE IMPACT</div>
                        <div className={`font-mono tabular-nums text-base font-semibold ${impact.price_delta >= 0 ? "text-red-600" : "text-emerald-600"}`}>
                            {impact.price_delta >= 0 ? "+" : ""}{fmtINR(impact.price_delta)}
                        </div>
                    </div>
                </div>

                <div>
                    <div className="eyebrow text-slate-500 mb-2">PARTS AFFECTED</div>
                    <table className="w-full text-[12px]">
                        <thead>
                            <tr className="border-b border-slate-200 text-slate-500">
                                <th className="text-left py-1.5">Part Number</th>
                                <th className="text-right py-1.5">Old Should-Cost</th>
                                <th className="text-right py-1.5">New Should-Cost</th>
                                <th className="text-right py-1.5">Δ</th>
                            </tr>
                        </thead>
                        <tbody>
                            {impact.parts_changed.map((p) => (
                                <tr key={p.zbc_id} className="border-b border-slate-100 align-top">
                                    <td className="py-1.5 font-mono font-semibold">
                                        {p.part_number}
                                        {p.rate_change_reason && (
                                            <div className="font-sans font-normal text-[11px] text-slate-500 mt-0.5 max-w-xs">
                                                <span className="font-semibold text-slate-600">
                                                    {p.rate_change_reason.grade_code}
                                                    {" "}₹{p.rate_change_reason.old_rate} → ₹{p.rate_change_reason.new_rate}/kg:
                                                </span>{" "}
                                                {p.rate_change_reason.reason || "No reason recorded."}
                                                {p.rate_change_reason.changed_by_name && (
                                                    <span> — {p.rate_change_reason.changed_by_name}</span>
                                                )}
                                            </div>
                                        )}
                                    </td>
                                    <td className="py-1.5 text-right font-mono tabular-nums">{fmtINR(p.old_should_cost)}</td>
                                    <td className="py-1.5 text-right font-mono tabular-nums">{fmtINR(p.new_should_cost)}</td>
                                    <td className={`py-1.5 text-right font-mono tabular-nums font-semibold ${p.delta >= 0 ? "text-red-600" : "text-emerald-600"}`}>
                                        {p.delta >= 0 ? "+" : ""}{fmtINR(p.delta)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {!impact.can_reprice && (
                    <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-[12px] text-slate-600">
                        This RFQ has already been finally decided, so re-pricing is disabled. This view is informational only.
                    </div>
                )}

                <div className="flex justify-end gap-2">
                    <SlateButton variant="outline" onClick={onClose}>Leave As-Is For Now</SlateButton>
                    {impact.can_reprice && (
                        <CopperButton onClick={onReprice} disabled={repricing} data-testid="rfq-reprice-btn">
                            {repricing ? "Re-pricing…" : "Re-price Now"}
                        </CopperButton>
                    )}
                </div>
            </div>
        </Modal>
    );
}

function WhatIfModal({ open, onClose, rfq }) {
    const [grades, setGrades] = useState([]);
    const [gradeId, setGradeId] = useState("");
    const [newRate, setNewRate] = useState("");
    const [conversionDeltaPct, setConversionDeltaPct] = useState(0);
    const [result, setResult] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!open) return;
        api.get("/steel-grades")
            .then((r) => {
                // Prefer grades actually used in this RFQ's parts
                const usedIds = new Set((rfq.parts || []).map((p) => p.steel_grade_id).filter(Boolean));
                const sorted = [...r.data].sort((a, b) => (usedIds.has(b.id) ? 1 : 0) - (usedIds.has(a.id) ? 1 : 0));
                setGrades(sorted);
                if (sorted.length > 0) {
                    setGradeId(sorted[0].id);
                    setNewRate(sorted[0].rate_per_kg || 0);
                }
            })
            .catch(() => { /* noop */ });
    }, [open, rfq]);

    const onGradeChange = (gid) => {
        setGradeId(gid);
        const g = grades.find((x) => x.id === gid);
        if (g) setNewRate(g.rate_per_kg || 0);
        setResult(null);
    };

    const hasRateChange = !!gradeId && !!newRate;
    const hasConversionChange = parseFloat(conversionDeltaPct || 0) !== 0;

    const runSimulation = () => {
        if (!hasRateChange && !hasConversionChange) {
            toast.error("Enter a new steel rate or a conversion cost change");
            return;
        }
        setBusy(true);
        api.post(`/rfqs/${rfq.id}/what-if`, {
            ...(hasRateChange ? { grade_id: gradeId, new_rate: parseFloat(newRate) } : {}),
            conversion_cost_delta_pct: parseFloat(conversionDeltaPct) || 0,
        })
            .then((r) => setResult(r.data))
            .catch((e) => toast.error(formatApiError(e)))
            .finally(() => setBusy(false));
    };

    const undo = () => { setResult(null); toast.info("Simulation cleared, RFQ unchanged"); };

    const selectedGrade = grades.find((g) => g.id === gradeId);
    const oldRate = selectedGrade?.rate_per_kg || 0;
    const delta = parseFloat(newRate || 0) - oldRate;

    return (
        <Modal
            open={open} onClose={onClose} size="lg"
            title={<span className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-[hsl(var(--copper))]" /> What-If Simulator</span>}
            footer={
                <>
                    <SlateButton variant="ghost" onClick={onClose}>Close</SlateButton>
                    {result && <SlateButton variant="outline" onClick={undo} data-testid="rfq-whatif-undo">Undo Simulation</SlateButton>}
                    <CopperButton onClick={runSimulation} disabled={busy || (!hasRateChange && !hasConversionChange)} data-testid="rfq-whatif-calculate">
                        {busy ? "Calculating…" : "Calculate Impact"}
                    </CopperButton>
                </>
            }
        >
            <div className="space-y-4 text-[13px]">
                <div className="rounded-md bg-[hsl(var(--copper-soft))]/30 border border-[hsl(var(--copper))]/20 px-3 py-2 text-[12px] text-slate-700">
                    <strong className="text-slate-900">Non-destructive simulation.</strong> Try a hypothetical steel-rate change and/or an overall conversion cost change, and see the impact on margin &amp; final price. The actual RFQ is never modified.
                </div>

                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <div className="eyebrow text-slate-500 mb-1">STEEL GRADE</div>
                        <select value={gradeId} onChange={(e) => onGradeChange(e.target.value)} className="w-full h-9 px-2.5 rounded-md border border-slate-200 text-[12px]" data-testid="rfq-whatif-grade">
                            {grades.map((g) => <option key={g.id} value={g.id}>{g.code} (current ₹{g.rate_per_kg}/kg)</option>)}
                        </select>
                    </div>
                    <div>
                        <div className="eyebrow text-slate-500 mb-1">NEW RATE (₹/kg)</div>
                        <input type="number" value={newRate} onChange={(e) => setNewRate(e.target.value)} step="0.01"
                               className="w-full h-9 px-2.5 rounded-md border border-slate-200 text-[12px] font-mono tabular-nums" data-testid="rfq-whatif-newrate" />
                        {delta !== 0 && (
                            <div className={`text-[11px] mt-1 font-mono ${delta > 0 ? "text-red-600" : "text-emerald-600"}`}>
                                {delta > 0 ? "+" : ""}{delta.toFixed(2)} vs current ₹{oldRate}
                            </div>
                        )}
                    </div>
                </div>

                <div>
                    <div className="eyebrow text-slate-500 mb-1">CONVERSION COST Δ% (ALL PARTS)</div>
                    <input type="number" value={conversionDeltaPct} onChange={(e) => setConversionDeltaPct(e.target.value)} step="1"
                           className="w-full h-9 px-2.5 rounded-md border border-slate-200 text-[12px] font-mono tabular-nums" data-testid="rfq-whatif-conversion-delta" />
                    <div className="text-[11px] text-slate-500 mt-1">Applies to every part's conversion, cutting and welding cost — e.g. +5 = 5% costlier across the board.</div>
                </div>

                {result && (
                    <div className="rounded-lg border border-slate-200 overflow-hidden" data-testid="rfq-whatif-result">
                        <div className="px-3 py-2 border-b border-slate-200 bg-slate-50/60 text-[11px] font-semibold text-slate-700">SIMULATED IMPACT</div>
                        <div className="grid grid-cols-2 divide-x divide-slate-200">
                            <div className="p-3">
                                <div className="eyebrow text-slate-500 mb-1">FINAL PRICE</div>
                                <div className="font-mono tabular-nums text-[15px] font-semibold">{fmtINR(result.simulated_pricing?.final_price || 0)}</div>
                                <div className={`text-[11px] font-mono mt-0.5 ${result.final_price_delta > 0 ? "text-emerald-600" : result.final_price_delta < 0 ? "text-red-600" : "text-slate-500"}`}>
                                    {result.final_price_delta > 0 ? "+" : ""}{fmtINR(result.final_price_delta)} vs current
                                </div>
                            </div>
                            <div className="p-3">
                                <div className="eyebrow text-slate-500 mb-1">MARGIN</div>
                                <div className={`font-mono tabular-nums text-[15px] font-semibold ${result.simulated_pricing?.margin_pct >= 12 ? "text-emerald-600" : result.simulated_pricing?.margin_pct >= 8 ? "text-amber-600" : "text-red-600"}`}>
                                    {fmtPct(result.simulated_pricing?.margin_pct || 0)}
                                </div>
                                <div className={`text-[11px] font-mono mt-0.5 ${result.margin_delta > 0 ? "text-emerald-600" : result.margin_delta < 0 ? "text-red-600" : "text-slate-500"}`}>
                                    {result.margin_delta > 0 ? "+" : ""}{result.margin_delta}pp vs current
                                </div>
                            </div>
                        </div>
                        <div className="px-3 py-2 bg-slate-50/40 text-[11px] text-slate-500 border-t border-slate-200">
                            Original RFQ pricing unchanged. Click <em>Undo Simulation</em> or just close this modal.
                        </div>
                    </div>
                )}
            </div>
        </Modal>
    );
}

function VolumeTiersModal({ open, onClose, rfq }) {
    const [volumesText, setVolumesText] = useState("10000, 50000, 100000");
    const [tiers, setTiers] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => { if (!open) { setTiers(null); } }, [open]);

    const run = () => {
        const volumes = volumesText.split(",").map((s) => parseInt(s.trim(), 10)).filter((n) => n > 0);
        if (volumes.length === 0) { toast.error("Enter at least one positive volume"); return; }
        setBusy(true);
        api.post(`/rfqs/${rfq.id}/volume-tiers`, { volumes })
            .then((r) => setTiers(r.data.tiers))
            .catch((e) => toast.error(formatApiError(e)))
            .finally(() => setBusy(false));
    };

    const currentFinal = rfq?.pricing?.final_price || 0;

    return (
        <Modal
            open={open} onClose={onClose} size="lg"
            title={<span className="flex items-center gap-2"><Layers className="h-4 w-4 text-[hsl(var(--copper))]" /> Volume Tiers</span>}
            footer={
                <>
                    <SlateButton variant="ghost" onClick={onClose}>Close</SlateButton>
                    <CopperButton onClick={run} disabled={busy} data-testid="rfq-volume-tiers-calculate">
                        {busy ? "Calculating…" : "Calculate Tiers"}
                    </CopperButton>
                </>
            }
        >
            <div className="space-y-4 text-[13px]">
                <div className="rounded-md bg-[hsl(var(--copper-soft))]/30 border border-[hsl(var(--copper))]/20 px-3 py-2 text-[12px] text-slate-700">
                    <strong className="text-slate-900">Non-destructive.</strong> Reprices this RFQ at each annual-volume breakpoint you enter, quoting a curve in one shot instead of negotiating volume round-by-round. Only parts with a tooling/CAPEX cost configured will actually vary between tiers; everything else in the formula doesn't change with volume.
                </div>
                <div>
                    <div className="eyebrow text-slate-500 mb-1">ANNUAL VOLUMES (comma-separated, up to 10)</div>
                    <input
                        value={volumesText} onChange={(e) => setVolumesText(e.target.value)}
                        className="w-full h-9 px-2.5 rounded-md border border-slate-200 text-[12px] font-mono tabular-nums"
                        placeholder="10000, 50000, 100000" data-testid="rfq-volume-tiers-input"
                    />
                </div>

                {tiers && (
                    <div className="rounded-lg border border-slate-200 overflow-hidden" data-testid="rfq-volume-tiers-result">
                        <table className="w-full text-[12px]">
                            <thead>
                                <tr className="border-b border-slate-200 bg-slate-50/60 text-slate-500">
                                    <th className="text-left py-2 px-3">Annual Volume</th>
                                    <th className="text-right py-2 px-3">Final Price</th>
                                    <th className="text-right py-2 px-3">Δ vs Current</th>
                                    <th className="text-right py-2 px-3">Margin</th>
                                </tr>
                            </thead>
                            <tbody>
                                {tiers.map((t) => {
                                    const delta = (t.pricing?.final_price || 0) - currentFinal;
                                    return (
                                        <tr key={t.annual_volume} className="border-b border-slate-100">
                                            <td className="py-2 px-3 font-mono">{t.annual_volume.toLocaleString("en-IN")}</td>
                                            <td className="py-2 px-3 text-right font-mono tabular-nums font-semibold">{fmtINR(t.pricing?.final_price || 0)}</td>
                                            <td className={`py-2 px-3 text-right font-mono tabular-nums ${delta > 0 ? "text-red-600" : delta < 0 ? "text-emerald-600" : "text-slate-400"}`}>
                                                {delta === 0 ? "—" : `${delta > 0 ? "+" : ""}${fmtINR(delta)}`}
                                            </td>
                                            <td className="py-2 px-3 text-right font-mono tabular-nums">{fmtPct(t.pricing?.margin_pct || 0)}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        <div className="px-3 py-2 bg-slate-50/40 text-[11px] text-slate-500 border-t border-slate-200">
                            Original RFQ pricing unchanged; this is a preview only.
                        </div>
                    </div>
                )}
            </div>
        </Modal>
    );
}
